import {
  type AllReadsQuery,
  DBSortOrder,
  DBTimestampType,
  HTTPFetchError,
  RacemapAPIClient,
} from '@racemap/utilities/api-client';
import { OneDayInMillis } from '@racemap/utilities/consts/time';
import type { Shadowtrack } from '@racemap/utilities/types/events';
import type { EventTrackObject } from '@racemap/utilities/types/geos';
import type { PreparedRead } from '@racemap/utilities/types/prediction';
import type { Ping } from '@racemap/utilities/types/types';
import { produce } from 'immer';
import type { StoreApi } from 'zustand';
import type { DraftState, State } from './reducers';

const apiClient = RacemapAPIClient.fromWindowLocation();

export type ReadsFilter = Omit<AllReadsQuery, 'startTime' | 'endTime'> & {
  startTime: Date;
  endTime: Date;
};

export interface PredictionState {
  prediction: {
    readsFilter: ReadsFilter;
    chunkedShadowTrack: EventTrackObject | null;
    // maybe load the simple shadowtrack to make the rendering in the map simpler
    actions: {
      loadShadowtrack: (eventId: string) => Promise<void>;
      setReadsFilter: (filter: Partial<ReadsFilter>) => void;
    };
  };
}

export const createPredictionStore = (set: StoreApi<State>['setState']): PredictionState => ({
  prediction: {
    chunkedShadowTrack: null,
    readsFilter: {
      eventId: '',
      startTime: new Date(new Date().valueOf() - 5 * OneDayInMillis),
      endTime: new Date(),
      timestampFilterType: DBTimestampType.ReceivedAt,
      timestampSortType: DBTimestampType.ReceivedAt,
      order: DBSortOrder.Descending,
      limit: 100,
      offset: 0,
      dummy: false,
    },
    actions: {
      loadShadowtrack: async (eventId) => {
        try {
          // TODO: load here later directly the splits!
          // const event = get().events.getter.currentEvent();
          // const simpleShadowtrack = get().events.getter.shadowtrack();
          // const splits = simpleShadowtrack?.properties.splits || [];
          const shadowtrackSimple = (await apiClient.getEventShadowGeo(eventId)).features[0];
          const shadowtrackCoordinatesChunked = await apiClient
            .asAnonymous()
            .getEventChunkedShadowGeo(eventId);
          if (shadowtrackSimple == null || shadowtrackCoordinatesChunked == null) return;

          const shadowtrack: Shadowtrack = {
            type: 'Feature',
            id: shadowtrackSimple.id,
            geometry: {
              type: 'LineString',
              coordinates: shadowtrackCoordinatesChunked,
            },
            properties: {
              ...shadowtrackSimple.properties,
              name: 'Chunked Shadowtrack',
            },
          };

          set(
            produce((s: DraftState) => {
              s.prediction.chunkedShadowTrack = shadowtrack;
            }),
          );
        } catch (err) {
          if (err instanceof HTTPFetchError) return;
          console.error(err);
        }
      },
      setReadsFilter: (filterPatch: Partial<ReadsFilter>) => {
        set(
          produce((s: DraftState) => {
            s.prediction.readsFilter = { ...s.prediction.readsFilter, ...filterPatch };
          }),
        );
      },
    },
  },
});

export const prepareRead = (read: Ping): PreparedRead => {
  return {
    readerId: read.boxId,
    userId: read.userId,
    customerId: read.customerId,
    pingType: read.pingType,
    count: read.hits,
    dropped: [],
    rssi: read.rssi || -300,
    transponderId: read.transponderId || '',
    receivedAt: new Date(read.receivedAt),
    timestamp: new Date(read.timestamp),
    wasUserDefined: read.wasUserDefined,
    timingName: read.timingName,
    location:
      read.lat != null && read.lng != null
        ? {
            state: read.pos_state,
            lat: read.lat,
            lng: read.lng,
            elv: read.elv,
          }
        : undefined,
  };
};
